import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { Layout } from "../components/Layout";
import { useAppConfig } from "../lib/AppConfigContext";
import { getConsulta, getResults, downloadHandVote } from "../lib/api";
import { toast } from "sonner";
import { OPTION_COLORS } from "../components/results/constants";
import { getOptionsArray } from "../components/results/dataUtils";
import { formatDateTimeTz } from "../lib/dateUtils";

import { ResultsHeader } from "../components/results/ResultsHeader";
import { ResultsTabs } from "../components/results/ResultsTabs";
import { GeneralTab } from "../components/results/GeneralTab";
import { SexTab } from "../components/results/SexTab";
import { AgeTab } from "../components/results/AgeTab";
import { SummaryTab } from "../components/results/SummaryTab";
import { ParticipationTab } from "../components/results/ParticipationTab";
import { AuditTab } from "../components/results/AuditTab";
import { AuditVerifyTab } from "../components/results/AuditVerifyTab";
import { BlockchainTab } from "../components/results/BlockchainTab";

export default function ResultsPage() {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { adminEmail: configAdminEmail } = useAppConfig();

  const [consulta, setConsulta] = useState(null);
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('general');
  const [visitedTabs, setVisitedTabs] = useState(new Set(['general']));
  const [optionColorMap, setOptionColorMap] = useState({});
  const [exportingPdf, setExportingPdf] = useState(false);

  const handleSetActiveTab = useCallback((tab) => {
    setActiveTab(tab);
    setVisitedTabs(prev => {
      if (prev.has(tab)) return prev;
      return new Set([...prev, tab]);
    });
  }, []);

  useEffect(() => { loadData(); }, [id]);

  useEffect(() => {
    if (consulta?.options) {
      const colorMap = {};
      consulta.options.forEach((option, index) => {
        const title = typeof option === 'string' ? option : option.title;
        colorMap[title] = OPTION_COLORS[index % OPTION_COLORS.length];
      });
      setOptionColorMap(colorMap);
    }
  }, [consulta]);

  const loadData = async () => {
    try {
      const [consultaRes, resultsRes] = await Promise.all([getConsulta(id), getResults(id)]);
      setConsulta(consultaRes.data);
      setResults(resultsRes.data);
    } catch {
      toast.error(t('errors.generic'));
      navigate(`/consultes/${id}`);
    } finally {
      setLoading(false);
    }
  };

  // Auto-download hand vote file after scrutiny
  useEffect(() => {
    if (searchParams.get('autoSendPdf') === 'true' && !loading && consulta?.enable_vot_ma_alcada && results) {
      const timer = setTimeout(async () => {
        try {
          const res = await downloadHandVote(id);
          const url = window.URL.createObjectURL(new Blob([res.data]));
          const link = document.createElement('a');
          link.href = url;
          const disposition = res.headers['content-disposition'] || '';
          const filenameMatch = disposition.match(/filename="?([^"]+)"?/);
          link.setAttribute('download', filenameMatch ? filenameMatch[1] : `vot_ma_alcada_${consulta?.title?.replace(/ /g, '_') || 'consulta'}.xlsx`);
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(url);
          toast.success(t('results.handVoteFileDownloaded'));
        } catch {}
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [searchParams, loading, consulta, results, id]);

  // Clean up autoSendPdf param
  useEffect(() => {
    if (searchParams.get('autoSendPdf') === 'true' && !loading && consulta && results) {
      searchParams.delete('autoSendPdf');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, loading, consulta, results, setSearchParams]);

  if (loading) {
    return <Layout><div className="flex items-center justify-center h-screen"><div className="spinner" /></div></Layout>;
  }

  const options = getOptionsArray(consulta);

  return (
    <Layout>
      <div className="content-container" data-testid="results-page">
        <ResultsHeader
          consulta={consulta} results={results} id={id}
          i18n={i18n} exportingPdf={exportingPdf}
          setExportingPdf={setExportingPdf} navigate={navigate}
        />

        <ResultsTabs activeTab={activeTab} setActiveTab={handleSetActiveTab} consulta={consulta} results={results} />

        <div style={{ display: activeTab === 'general' ? 'block' : 'none' }}>
          <GeneralTab results={results} options={options} optionColorMap={optionColorMap} consulta={consulta} />
        </div>

        {consulta?.enable_stats_sex !== false && visitedTabs.has('sex') && (
          <div style={{ display: activeTab === 'sex' ? 'block' : 'none' }}>
            <SexTab results={results} options={options} optionColorMap={optionColorMap} />
          </div>
        )}

        {consulta?.enable_stats_age !== false && visitedTabs.has('age') && (
          <div style={{ display: activeTab === 'age' ? 'block' : 'none' }}>
            <AgeTab results={results} options={options} optionColorMap={optionColorMap} />
          </div>
        )}

        {visitedTabs.has('summary') && (
          <div style={{ display: activeTab === 'summary' ? 'block' : 'none' }}>
            <SummaryTab results={results} options={options} optionColorMap={optionColorMap} consulta={consulta} />
          </div>
        )}

        {visitedTabs.has('participation') && (
          <div style={{ display: activeTab === 'participation' ? 'block' : 'none' }}>
            <ParticipationTab results={results} consulta={consulta} id={id} />
          </div>
        )}

        {consulta?.enable_vot_ma_alcada && visitedTabs.has('audit') && (
          <div style={{ display: activeTab === 'audit' ? 'block' : 'none' }}>
            <AuditTab consulta={consulta} id={id} navigate={navigate} />
          </div>
        )}

        {consulta?.enable_audit && visitedTabs.has('audit_verify') && (
          <div style={{ display: activeTab === 'audit_verify' ? 'block' : 'none' }}>
            <AuditVerifyTab consulta={consulta} id={id} />
          </div>
        )}

        {results?.blockchain_enabled && visitedTabs.has('blockchain') && (
          <div style={{ display: activeTab === 'blockchain' ? 'block' : 'none' }}>
            <BlockchainTab results={results} onRefresh={loadData} />
          </div>
        )}

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }} className="mt-6 text-sm text-slate-500">
          <p>{t('results.scrutinyDone')}: {results?.scrutiny_date ? formatDateTimeTz(results.scrutiny_date, consulta?.entity_timezone) : t('results.notAvailable')}</p>
        </motion.div>
      </div>
    </Layout>
  );
}
